import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Mail, Plus, Trash2, X, Building2, Globe } from "lucide-react";

const RUOLI = ["Operatore studio", "Committente", "Tecnico", "Datore di lavoro", "RSPP", "CSE", "Altro"];
const SOGLIE = [["expired", "Scaduti"], ["15", "15 gg"], ["30", "30 gg"], ["60", "60 gg"]];
const ALL_SOGLIE = SOGLIE.map(s => s[0]);

export default function RecipientsPanel() {
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const [r, c] = await Promise.all([api.get("/recipients"), api.get("/companies")]);
    setItems(r.data); setCompanies(c.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (r, patch) => {
    await api.put(`/recipients/${r.id}`, { email: r.email, nome: r.nome, ruolo: r.ruolo, company_id: r.company_id || "", attivo: r.attivo !== false, soglie: r.soglie || ALL_SOGLIE, ...patch });
    load();
  };
  const toggle = (r) => save(r, { attivo: r.attivo === false });
  const toggleSoglia = (r, s) => {
    const cur = r.soglie || ALL_SOGLIE;
    const next = cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s];
    if (!next.length) { toast.error("Seleziona almeno una soglia"); return; }
    save(r, { soglie: next });
  };
  const remove = async (r) => {
    if (!window.confirm(`Rimuovere ${r.email} dai destinatari?`)) return;
    await api.delete(`/recipients/${r.id}`);
    toast.success("Destinatario rimosso"); load();
  };

  return (
    <section className="space-y-3" data-testid="recipients-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><Mail size={18} /> Destinatari promemoria scadenze</h2>
          <p className="text-sm text-slate-500">Oltre agli admin, ricevono le email di scadenza: destinatari globali (tutte le aziende) o legati a una singola azienda.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="add-recipient"
          className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-3 py-2 rounded-lg text-sm font-medium">
          <Plus size={16} /> Aggiungi destinatario
        </button>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Nome</th>
              <th className="text-left px-4 py-3 font-medium">Email</th>
              <th className="text-left px-4 py-3 font-medium">Ruolo</th>
              <th className="text-left px-4 py-3 font-medium">Ambito</th>
              <th className="text-left px-4 py-3 font-medium">Avvisi</th>
              <th className="text-left px-4 py-3 font-medium">Attivo</th>
              <th className="text-right px-4 py-3 font-medium">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid="recipient-row">
                <td className="px-4 py-3 font-medium">{r.nome}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.email}</td>
                <td className="px-4 py-3">{r.ruolo || "—"}</td>
                <td className="px-4 py-3">
                  {r.company_id ? <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-800 px-2 py-0.5 rounded-full"><Building2 size={12} /> {r.company_name}</span>
                    : <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full"><Globe size={12} /> Tutte le aziende</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {SOGLIE.map(([v, l]) => {
                      const on = (r.soglie || ALL_SOGLIE).includes(v);
                      return <button key={v} onClick={() => toggleSoglia(r, v)} data-testid={`recipient-soglia-${v}`}
                        className={`px-2 py-0.5 rounded-full text-xs border ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-400 border-slate-300"}`}>{l}</button>;
                    })}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <input type="checkbox" checked={r.attivo !== false} onChange={() => toggle(r)} data-testid="recipient-toggle" />
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => remove(r)} className="text-red-600 p-1.5"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">Nessun destinatario aggiuntivo: i promemoria arrivano solo agli admin.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <RecipientDialog companies={companies} onClose={() => setOpen(false)} onCreated={load} />}
    </section>
  );
}

function RecipientDialog({ companies, onClose, onCreated }) {
  const [f, setF] = useState({ nome: "", email: "", ruolo: "Operatore studio", company_id: "", soglie: ALL_SOGLIE });
  const toggleS = (s) => setF({ ...f, soglie: f.soglie.includes(s) ? f.soglie.filter(x => x !== s) : [...f.soglie, s] });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.soglie.length) { toast.error("Seleziona almeno una soglia di avviso"); return; }
    setBusy(true);
    try {
      await api.post("/recipients", { ...f, attivo: true });
      toast.success("Destinatario aggiunto"); onCreated(); onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore");
    } finally { setBusy(false); }
  };
  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg";
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-display font-semibold text-lg">Nuovo destinatario</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Nome *</label>
            <input required value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} className={inp} data-testid="recipient-name" /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Email *</label>
            <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} data-testid="recipient-email" /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Ruolo</label>
            <select value={f.ruolo} onChange={(e) => setF({ ...f, ruolo: e.target.value })} className={inp} data-testid="recipient-role">
              {RUOLI.map(r => <option key={r}>{r}</option>)}
            </select></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Ambito</label>
            <select value={f.company_id} onChange={(e) => setF({ ...f, company_id: e.target.value })} className={inp} data-testid="recipient-company">
              <option value="">Tutte le aziende (globale)</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale}</option>)}
            </select></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Quali avvisi ricevere</label>
            <div className="flex flex-wrap gap-2">
              {SOGLIE.map(([v, l]) => (
                <label key={v} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer ${f.soglie.includes(v) ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700"}`}>
                  <input type="checkbox" className="hidden" checked={f.soglie.includes(v)} onChange={() => toggleS(v)} data-testid={`new-recipient-soglia-${v}`} />{l}
                </label>
              ))}
            </div></div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600">Annulla</button>
            <button type="submit" disabled={busy} data-testid="recipient-submit" className="px-4 py-2 bg-slate-900 text-white rounded-lg disabled:opacity-60">Aggiungi</button>
          </div>
        </form>
      </div>
    </div>
  );
}
